/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, useCallback } from 'react';
import Box from '@mui/material/Box';
import { WalletDetails, currency_symbols } from '../../../typings/wallet';
import QSModal from '../modal';
import { QSButton, QSTypography } from '../../atoms';
import QSIconButton from '../../atoms/icon-button';
import CloseIcon from '../../../icons/Close';
import { ReactComponent as WalletIcon } from '../../../icons/wallet-icon.svg';
import { ReactComponent as AdIcon } from '../../../icons/ad-icon.svg';
import AlertIcon from '../../../icons/AlertIcon';
import QSTooltip from '../tooltip';
import { openPopup } from '../../../utils/popup';
import requestConfig from '../../../config/request';
import { getFirebaseToken } from '../../../../infra/auth/firebase-auth';
import { getUserChannelData } from '../../../../modules/login/v1/utility';

interface WalletDetailsModalProps {
  open: boolean;
  onClose: () => void;
  walletData: WalletDetails;
  loading?: boolean;
}

const WalletDetailsModal: FC<WalletDetailsModalProps> = ({ open, onClose, walletData, loading = false }) => {
  const channelPartnerName = getUserChannelData()?.name;
  // Use blockedBalance from API if available, otherwise fallback to calculated amount
  const blockedAmount =
    walletData.blockedBalance !== undefined ? walletData.blockedBalance : Math.max(walletData.balance * 0.1, 5);
  // Use totalBalance from API if available, otherwise calculate it
  const totalBalance =
    walletData.totalBalance !== undefined ? walletData.totalBalance : walletData.balance + blockedAmount;

  const handleRecharge = useCallback(async () => {
    if (!walletData || loading) return;
    if (walletData.generatePaymentLink) {
      const token = await getFirebaseToken();
      openPopup(`${requestConfig.dtCallbackUrl}/wallet?token=${token}`, '_self');
    } else {
      window.open(walletData.rechargeLink, '_blank');
    }
  }, [walletData, loading]);

  return (
    <QSModal open={open} onClose={onClose}>
      <Box
        css={css`
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 454px;
          background: white;
          border-radius: 8px;
          outline: none;
          display: flex;
          flex-direction: column;
          box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
        `}
      >
        {/* Title Bar */}
        <Box
          css={css`
            height: 52px;
            padding: 12px 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #f0f0f0;
          `}
        >
          <QSTypography
            css={css`
              font-size: 18px;
              font-weight: 600;
              color: #2d2d2d;
            `}
          >
            {channelPartnerName || 'DoubleTick'} Wallet
          </QSTypography>
          <QSIconButton
            onClick={onClose}
            css={css`
              padding: 4px;
              box-shadow: none;
              &:hover {
                background-color: #f5f5f5;
                box-shadow: none;
              }
              &:focus {
                box-shadow: none;
              }
            `}
          >
            <CloseIcon size={20} color="#7A7A7A" />
          </QSIconButton>
        </Box>

        {/* Content Area */}
        <Box
          css={css`
            flex: 1;
            padding: 20px;
            display: flex;
            flex-direction: column;
            gap: 16px;
            background-color: #fbfbfc;
          `}
        >
          {/* Card 1: Total Wallet Balance */}
          <Box
            css={css`
              padding: 16px;
              border: 1px solid #e8e8e8;
              border-radius: 8px;
              display: flex;
              align-items: flex-start;
              gap: 12px;
              background-color: #ffffff;
            `}
          >
            <WalletIcon />
            <Box
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <QSTypography
                css={css`
                  color: #7a7a7a;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 24px;
                `}
              >
                Total Wallet Balance
              </QSTypography>
              <QSTypography
                css={css`
                  color: #2d2d2d;
                  font-size: 24px;
                  font-weight: 500;
                `}
              >
                {currency_symbols[walletData.currencyCode] || walletData.currencyCode}
                {totalBalance.toFixed(2)}
              </QSTypography>
            </Box>
          </Box>

          {/* Card 2: Total amount reserved for ads */}
          <Box
            css={css`
              padding: 16px;
              border: 1px solid #e8e8e8;
              border-radius: 8px;
              display: flex;
              align-items: flex-start;
              gap: 12px;
              background-color: #ffffff;
            `}
          >
            <AdIcon />
            <Box
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                flex: 1;
              `}
            >
              <Box
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                `}
              >
                <QSTypography
                  css={css`
                    color: #7a7a7a;
                    font-size: 14px;
                    font-weight: 500;
                    line-height: 24px;
                  `}
                >
                  Total amount reserved for ads
                </QSTypography>
                <QSTooltip
                  title="This amount is reserved to keep your ads running smoothly without interruptions when they go live."
                  arrow
                  placement="top"
                >
                  <Box
                    css={css`
                      cursor: help;
                    `}
                  >
                    <AlertIcon width="16px" height="16px" color="#7a7a7a" />
                  </Box>
                </QSTooltip>
              </Box>
              <QSTypography
                css={css`
                  color: #2d2d2d;
                  font-size: 24px;
                  font-weight: 500;
                  line-height: 15px;
                `}
              >
                {currency_symbols[walletData.currencyCode] || walletData.currencyCode}
                {blockedAmount.toFixed(2)}
                <span
                  css={css`
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 1;
                  `}
                >
                  {' '}
                  reserved to keep your ads running smoothly without interruptions when it goes live.
                </span>
              </QSTypography>
            </Box>
          </Box>
        </Box>

        {/* Action Bar */}
        <Box
          css={css`
            padding: 20px;
            border-top: 1px solid #f0f0f0;
            background-color: white;
            display: flex;
            justify-content: flex-end;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
          `}
        >
          <QSButton
            variant="contained"
            onClick={handleRecharge}
            disabled={loading}
            css={css`
              background-color: #3f906d;
              color: white;
              padding: 8px 24px;
              font-size: 14px;
              font-weight: 600;
              text-transform: none;
              &:hover {
                background-color: #2c644c;
              }
              &:disabled {
                background-color: #cccccc;
                color: #666666;
              }
            `}
          >
            Recharge Wallet
          </QSButton>
        </Box>
      </Box>
    </QSModal>
  );
};

export default WalletDetailsModal;
